import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery } from '@apollo/client';
import { RefreshCw } from 'lucide-react';
import { INSIGHTS_QUERY } from '../graphql/queries';
import { REFRESH_INSIGHTS_MUTATION } from '../graphql/mutations';
import { FOCUS_RING_CLASSES } from '../lib/focusTrap';
import { Markdown } from './Markdown';

interface InsightsData {
  insights: {
    content: string | null;
    updatedAt: string | null;
  };
}

interface RefreshInsightsData {
  refreshInsights: {
    status: 'GENERATED' | 'RATE_LIMITED' | 'INCOMPLETE';
    content: string | null;
    updatedAt: string | null;
  };
}

/** Transient, non-error status surfaced in the polite live region. */
type Transient = 'idle' | 'rate_limited' | 'incomplete' | 'updated' | 'error';

const RATE_LIMITED_COPY =
  'Insights are up to date for today. Check back tomorrow for a fresh summary.';
const INCOMPLETE_COPY = "That didn't finish. You can try again.";
const ERROR_COPY = 'Something went wrong. Try again in a moment.';
const EMPTY_COPY =
  'No insights yet. As you capture moments, I build a short summary of the patterns across them.';

/**
 * Removes insights.md chrome before rendering: a leading YAML front-matter
 * fence and a single leading Markdown title line. The remaining body is passed
 * to <Markdown> so headings/lists/task items render as real elements.
 */
function stripInsightsChrome(raw: string): string {
  let text = raw.replace(/^\uFEFF/, '');
  const frontMatter = text.match(/^---[ \t]*\r?\n[\s\S]*?\r?\n---[ \t]*(?:\r?\n|$)/);
  if (frontMatter) {
    text = text.slice(frontMatter[0].length);
  }
  // Drop leading blank lines, then a single leading "# …" / "## …" title line.
  text = text.replace(/^(?:[ \t]*\r?\n)+/, '');
  text = text.replace(/^#{1,2}[ \t]+[^\n]*(?:\r?\n|$)/, '');
  return text;
}

/**
 * The body's first section (everything before the second `##` heading), for
 * the Home highlight card — Home stays calm instead of hosting the whole
 * document (design review P2.3). Returns the trimmed slice and whether more
 * sections exist ("Read all insights" links to the full document).
 */
function firstSection(body: string): { text: string; hasMore: boolean } {
  const sections = body.split(/\n(?=##\s)/);
  return { text: sections[0]?.trim() ?? '', hasMore: sections.length > 1 };
}

function formatUpdated(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const now = new Date();
  const y = new Date(now);
  y.setDate(now.getDate() - 1);
  if (d.toDateString() === now.toDateString()) return 'Updated today';
  if (d.toDateString() === y.toDateString()) return 'Updated yesterday';
  return `Updated ${d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}`;
}

/**
 * "Insights" card on Home: renders the caller's current insights.md summary and
 * offers a secondary refresh affordance wired to refreshInsights. Self-contained
 * (owns its query + mutation), takes no props.
 */
export function InsightsSummary() {
  const { data, loading: queryLoading } = useQuery<InsightsData>(INSIGHTS_QUERY);
  const [transient, setTransient] = useState<Transient>('idle');

  const [refresh, { loading }] = useMutation<RefreshInsightsData>(REFRESH_INSIGHTS_MUTATION, {
    onError: () => setTransient('error'),
    onCompleted: (result) => {
      const status = result?.refreshInsights?.status;
      if (status === 'RATE_LIMITED') setTransient('rate_limited');
      else if (status === 'INCOMPLETE') setTransient('incomplete');
      else setTransient('updated');
    },
    update: (cache, { data: result }) => {
      const payload = result?.refreshInsights;
      if (payload?.status !== 'GENERATED') return;
      // Write the fresh blob so the resting state matches after a remount.
      cache.writeQuery({
        query: INSIGHTS_QUERY,
        data: {
          insights: {
            __typename: 'InsightsDocument',
            content: payload.content,
            updatedAt: payload.updatedAt,
          },
        },
      });
    },
  });

  const content = data?.insights?.content ?? null;
  const body = content ? stripInsightsChrome(content).trim() : '';
  const highlight = firstSection(body);
  const hasBody = body.length > 0;
  const meta = hasBody ? formatUpdated(data?.insights?.updatedAt ?? null) : null;

  const buttonLabel = hasBody ? 'Update insights' : 'Generate insights';

  let statusText = '';
  if (loading) statusText = 'Updating insights…';
  else if (transient === 'rate_limited') statusText = RATE_LIMITED_COPY;
  else if (transient === 'incomplete') statusText = INCOMPLETE_COPY;
  else if (transient === 'updated') statusText = 'Insights updated.';

  const handleRefresh = (): void => {
    if (loading) return;
    setTransient('idle');
    void refresh();
  };

  return (
    <section
      className="glass-panel rounded-2xl p-5 sm:p-6 border border-[var(--card-border)] shadow-soft animate-fade-in-up"
      aria-label="Insights"
    >
      <div className="flex items-baseline justify-between mb-4">
        <h2 className="text-lg font-semibold text-[var(--text-primary)]">Insights</h2>
        <button
          type="button"
          onClick={handleRefresh}
          disabled={loading}
          aria-label={loading ? 'Updating…' : buttonLabel}
          className="btn btn-ghost text-sm gap-2 px-3 py-1.5"
        >
          {loading ? (
            <span
              aria-hidden
              className="animate-spin rounded-full h-4 w-4 border-b-2 border-[var(--primary)]"
            />
          ) : (
            <RefreshCw className="w-4 h-4" aria-hidden />
          )}
          {loading ? 'Updating…' : buttonLabel}
        </button>
      </div>

      {hasBody ? (
        <>
          <Markdown
            content={highlight.text}
            className="leading-relaxed text-[var(--text-primary)] text-sm sm:text-base"
          />
          <div className="mt-3 flex items-baseline justify-between gap-3">
            {meta ? <p className="text-xs text-[var(--text-tertiary)]">{meta}</p> : <span />}
            {highlight.hasMore && (
              <Link
                to="/docs/insights.md"
                className={`text-sm font-medium text-[var(--accent-ink)] shrink-0 ${FOCUS_RING_CLASSES}`}
              >
                Read all insights
              </Link>
            )}
          </div>
        </>
      ) : queryLoading ? (
        <div className="space-y-2.5" aria-hidden="true">
          <span className="skeleton block h-4 w-3/4 rounded-md" />
          <span className="skeleton block h-4 w-full rounded-md" />
          <span className="skeleton block h-4 w-5/6 rounded-md" />
          <span className="skeleton block h-4 w-2/3 rounded-md" />
        </div>
      ) : (
        <p className="text-sm text-[var(--text-tertiary)]">{EMPTY_COPY}</p>
      )}

      <p
        role="status"
        aria-live="polite"
        aria-busy={loading}
        className="mt-3 text-sm text-[var(--text-tertiary)]"
      >
        {statusText}
      </p>

      {transient === 'error' && (
        <div
          role="alert"
          className="mt-3 bg-[color-mix(in_oklab,var(--coral-400)_10%,transparent)] border border-[color-mix(in_oklab,var(--coral-400)_35%,transparent)] text-[var(--text-primary)] px-4 py-3 rounded-xl"
        >
          {ERROR_COPY}
        </div>
      )}
    </section>
  );
}
